import { DateRange } from "@enums/DateRange.enum";
import { DistanceMeasurement } from "@enums/measurements/DistanceMeasurement.enum";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { IVehicleFilter } from "@interfaces/filters/IVehicleFilter";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IJourney } from "@interfaces/IJourney";
import { IPlot } from "@interfaces/IPlot";
import { DriverService } from "@services/driver.service";
import { JourneyService } from "@services/journey.service";
import { PlotService } from "@services/plot.service";
import { JourneyCostType, PreferencesService } from "@services/preferences.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { DynamicReportService, ExportType, IStandardColumns } from "../../_services/dynamicReports.service";
import { ColumnDataType } from "../../enums/ColumnDataType";
import { IColumnHeader } from "../../models/IColumnHeader";
import { IReportDefinition } from "../../models/IReportDefinition";
import { IReportParameters } from "../../models/IReportParameters";
import { IReportRow } from "../../models/IReportRow";
import { IReportRowCell } from "../../models/IReportRowCell";
import { IReportTableDefinition } from "../../models/IReportTableDefinition";
import "./expenses.scss";

export interface IExpenseComponentJourney extends IJourney {
    startPlot?: IPlot;
    endPlot?: IPlot;
    updating?: boolean;
}

class ComponentController implements angular.IComponentController {
    dateFilter: IDateFilter;
    driverFilter: IDriverFilter;
    vehicleFilter: IVehicleFilter;
    groupFilter: IGroupFilter;

    loading: boolean;

    journeys: IExpenseComponentJourney[];
    displayJourneys: IExpenseComponentJourney[];

    standardColumns: IStandardColumns;
    preferences: ICombinedPreferences;

    defaultDateRange = DateRange.Today;

    totalExpensedJourneys: number = 0;
    totalCost: number;
    totalDistance: number;
    totalDuration: number;
    totalFuelUsed: number;

    showDownloadOptions: boolean;
    downloadOptions: string[] = [
        'pdf',
        'excel',
        'csv'
    ];

    static $inject = ["$q", "$mdToast", "journeyService", "veriDate", "vehicleService", "driverService", "dynamicReportService", "plotService", "preferencesService", "translationService","unitConversionService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $mdToast: angular.material.IToastService,
        private readonly journeyService: JourneyService,
        private readonly veriDate: VeriDate,
        private readonly vehicleService: VehicleService,
        private readonly driverService: DriverService,
        private readonly dynamicReportService: DynamicReportService,
        private readonly plotService: PlotService,
        private readonly preferencesService: PreferencesService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService
    ) {
    }

    $onInit() {
        this.dynamicReportService.getStandardColumns().then((standardColumns) => {
            this.standardColumns = standardColumns;
        });
    }

    $onChanges() {
        this.loadData();
    }

    loadData() {
        if (!this.dateFilter) {
            const dateRange = this.veriDate.dateRangeFromEnum(this.defaultDateRange);
            this.dateFilter = {
                start: dateRange.start,
                end: dateRange.end
            }
        }

        this.loading = true;

        let journeysPromise: angular.IPromise<IJourney[]>;

        if (this.vehicleFilter?.vehicle) {
            journeysPromise = this.journeyService.getJourneysForVehicle(this.vehicleFilter.vehicle.Id, this.dateFilter.start, this.dateFilter.end);
        }
        else if (this.driverFilter?.driver) {
            journeysPromise = this.journeyService.getJourneysForDriver(this.driverFilter.driver.Id, this.dateFilter.start, this.dateFilter.end);
        }
        else {
            journeysPromise = this.journeyService.getAllJourneys(this.dateFilter.start, this.dateFilter.end, false, true, false, true);
        }

        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const driversPromise = this.driverService.getDrivers();

        let preferencesPromise = this.$q.resolve();
        if (!this.preferences) {
            preferencesPromise = this.preferencesService.getAllPreferences().then((preferences) => {
                this.preferences = preferences;
            });
        }

        this.$q.all([journeysPromise, vehiclesPromise, driversPromise, preferencesPromise]).then(
            ([journeys, vehicles, drivers]) => {
                this.journeys = journeys;
                this.filterJourneys();
                this.journeyService.mapJourneysToResources(this.displayJourneys, vehicles, drivers);
                this.calculateJourneysCost();
                this.getStartAndEndPlotsForJourneys();
                this.loading = false;
            });
    }

    journeyClicked(journey: IExpenseComponentJourney) {
        if (journey.updating) {
            return;
        }

        journey.Expensed = !journey.Expensed;
        journey.updating = true;

        this.journeyService.updateJourneyExpense(journey).finally(() => {
            journey.updating = false;
        });

        this.calculateTotals();
    }

    selectNone() {
        this.updateAllJourneys(false);
    }

    selectAll() {
        this.updateAllJourneys(true);
    }

    selectWeekdays() {
        this.loading = true;

        var journeysToUpdate: IExpenseComponentJourney[] = [];

        this.displayJourneys.forEach(journey => {
            var startMoment = moment(journey.StartUtc);


            if (journey.Expensed && (startMoment.day() > 0 && startMoment.day() < 6) || startMoment.day() === 7) {
                // Deselect before reselecting
                journey.Expensed = false;
            }

            if (!journey.Expensed && (startMoment.day() > 0 && startMoment.day() < 6) || startMoment.day() === 7) {
                journey.Expensed = true;
                journey.updating = true;
                journeysToUpdate.push(journey);
            }
        });

        this.calculateTotals();

        if (journeysToUpdate.length === 0) {
            this.$mdToast.showSimple(this.translationService.t("Expenses.NoWeekday"));
        } else {
            this.$mdToast.showSimple(this.translationService.t("Expenses.JourneysSelected", journeysToUpdate.length));
        }


        return this.updateJourneys(journeysToUpdate);
    }

    selectWeekends() {
        this.loading = true;

        var journeysToUpdate: IExpenseComponentJourney[] = [];

        this.displayJourneys.forEach(journey => {
            var startMoment = moment(journey.StartUtc);

            if (journey.Expensed && startMoment.day() === 0 || startMoment.day() === 6) {
                // Deselect before reselecting
                journey.Expensed = false;
            }

            if (!journey.Expensed && startMoment.day() === 0 || startMoment.day() === 6) {
                journey.Expensed = true;
                journey.updating = true;
                journeysToUpdate.push(journey);
            }
        });

        this.calculateTotals();

        if (journeysToUpdate.length === 0) {
            this.$mdToast.showSimple(this.translationService.t("Expenses.NoWeekend"));
        } else {
            this.$mdToast.showSimple(this.translationService.t("Expenses.JourneysSelected", journeysToUpdate.length));
        }

        return this.updateJourneys(journeysToUpdate);
    }

    updateAllJourneys(expensed: boolean): angular.IPromise<void> {
        this.loading = true;

        var journeysToUpdate: IExpenseComponentJourney[] = [];

        this.displayJourneys.forEach(journey => {
            if (journey.Expensed !== expensed) {
                journey.Expensed = expensed;
                journey.updating = true;
                journeysToUpdate.push(journey);
            }
        });

        this.calculateTotals();
        if (expensed) {
            this.$mdToast.showSimple(this.translationService.t("Expenses.AllSelected"));

        } else {
            this.$mdToast.showSimple(this.translationService.t("Expenses.AllDeselected"));

        }

        return this.updateJourneys(journeysToUpdate);
    }

    updateJourneys(journeysToUpdate: IExpenseComponentJourney[]): angular.IPromise<void> {
        return this.journeyService.updateJourneysExpenses(journeysToUpdate).then(() => {
            journeysToUpdate.forEach(journey => {
                journey.updating = false;
            });
                this.loading = false;
        });
    }

    onPreferenceChanged() {
        if (this.preferences.JourneyCostType) {
            this.preferences.JourneyCostType.Value = Number(this.preferences.JourneyCostType.Value);
        }

        this.calculateJourneysCost();
    }

    onShowDownloadOptions() {
        this.showDownloadOptions = !this.showDownloadOptions;
    }

    exportData(option: string) {
        this.loading = true;

        const reportDefinition: IReportDefinition = {
            TableDefinition: this.createReportTable(),
            Parameters: this.createReportParameters(),
            Title: "Expenses",
            Portrait: false
        };

        let exportType = ExportType.PDF;

        switch (option) {
            case 'csv':
                exportType = ExportType.CSV;
                break;

            case 'excel':
                exportType = ExportType.Excel;
                break;
        }

        this.dynamicReportService.generate(reportDefinition, exportType).then(() => {
            this.loading = false;
            this.showDownloadOptions = false;
        });
    }

    dateFilterChanged(filter: IDateFilter) {
        this.dateFilter = filter;
        this.loadData();
    }

    vehicleFilterChanged(filter: IVehicleFilter) {
        this.vehicleFilter = filter;
        if (this.vehicleFilter?.vehicle == null) {
            this.loadData();
        } else {
            this.filterJourneys();
        }
    }

    driverFilterChanged(filter: IDriverFilter) {
        this.driverFilter = filter;
        if (this.driverFilter?.driver == null) {
            this.loadData();
        } else {
            this.filterJourneys();
        }
    }

    groupFilterChanged(filter: IGroupFilter) {
        this.groupFilter = filter;
        this.filterJourneys();
    }

    // ---

    private getStartAndEndPlotsForJourneys() {
        this.displayJourneys.forEach(journey => {
            journey.startPlot = { Id: journey.StartPlotId }
            journey.endPlot = { Id: journey.EndPlotId }
        });
    }

    private createReportParameters(): IReportParameters {
        const group = this.groupFilter ? this.groupFilter.group : null;
        if (group) {
            group.parent = null;
            group.children = null;
        }

        return {
            Start: this.dateFilter.start,
            End: this.dateFilter.end,
            DriverId: this.driverFilter && this.driverFilter.driver ? this.driverFilter.driver.Id : null,
            VehicleId: this.vehicleFilter && this.vehicleFilter.vehicle ? this.vehicleFilter.vehicle.Id : null,
            VehicleGroupId: this.groupFilter && this.groupFilter.group ? this.groupFilter.group.Id : null,
            DriverGroupId: this.groupFilter && this.groupFilter.group ? this.groupFilter.group.Id : null,
            Driver: this.driverFilter ? this.driverFilter.driver : null,
            Vehicle: this.vehicleFilter ? this.vehicleFilter.vehicle : null,
            VehicleGroup: group,
            DriverGroup: group
        }
    }

    private createReportTable(): IReportTableDefinition {
        const report: IReportTableDefinition = {
            Headers: this.createReportHeaders(),
            Rows: this.createReportRows(),
            TotalsRow: this.createTotalsRow()
        };

        return report;
    }

    private createReportHeaders(): IColumnHeader[] {

        const headers: IColumnHeader[] = [];

        if (!this.vehicleFilter || !this.vehicleFilter.vehicle) {
            headers.push(this.createHeader("Vehicle", this.standardColumns.stringColumn))
        }

        if (!this.driverFilter || !this.driverFilter.driver) {
            headers.push(this.createHeader("Driver", this.standardColumns.stringColumn))
        }

        headers.push(this.createHeader("Start Time", this.standardColumns.dateColumn))
        headers.push(this.createHeader("Start Location", this.standardColumns.stringColumn))
        headers.push(this.createHeader("End Time", this.standardColumns.dateColumn))
        headers.push(this.createHeader("End Location", this.standardColumns.stringColumn))
        headers.push(this.createHeader("Distance", this.standardColumns.stringColumn))
        headers.push(this.createHeader("Duration", this.standardColumns.stringColumn))
        headers.push(this.createHeader("Fuel used", this.standardColumns.stringColumn))
        headers.push(this.createHeader("Cost", this.standardColumns.stringColumn))

        return headers;
    }

    private createHeader(title: string, template: IColumnHeader): IColumnHeader {
        const header = copy(template);
        header.Title = title;
        return header;
    }

    private createReportRows(): IReportRow[] {
        const reportRows: IReportRow[] = [];
        this.displayJourneys.forEach(journey => {
            if (journey.Expensed) {
                const reportRow: IReportRow = {
                    Parameters: null,
                    Cells: this.createExpenseJourneyCells(journey),
                    HasData: true,
                };

                reportRows.push(reportRow);
            }
        })

        return reportRows;
    }

    private createTotalsRow(): IReportRow {
        const row: IReportRow = {
            Parameters: null,
            Cells: this.createExpenseTotalsCells(),
            HasData: true,
        }

        return row;
    }

    private createExpenseTotalsCells(): IReportRowCell[] {
        const reportCells: IReportRowCell[] = [];

        reportCells.push(this.createCell(`Total: `, ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(`Cost type: ${this.preferences.JourneyCostType.Value == 0 ? "Distance" : "Hourly"}`, ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(`Cost Value: ${this.unitConversionService.convertUnitWithSuffix(this.preferences.JourneyCost.Value, UnitConversionType.Currency, 2)}`, ColumnDataType.String, this.standardColumns.stringColumn))

        if (!this.vehicleFilter || !this.vehicleFilter.vehicle) {
            reportCells.push(this.createCell("", ColumnDataType.String, this.standardColumns.stringColumn))
        }

        if (!this.driverFilter || !this.driverFilter.driver) {
            reportCells.push(this.createCell("", ColumnDataType.String, this.standardColumns.stringColumn))
        }

        reportCells.push(this.createCell("", ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(this.totalDistance, UnitConversionType.Distance, 2), ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(this.totalDuration, UnitConversionType.Time), ColumnDataType.Duration, this.standardColumns.durationColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(this.totalFuelUsed, UnitConversionType.FuelCapacity, 2), ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(this.totalCost, UnitConversionType.Currency, 2), ColumnDataType.String, this.standardColumns.stringColumn))

        return reportCells;
    }

    private createExpenseJourneyCells(journey: IExpenseComponentJourney): IReportRowCell[] {
        const reportCells: IReportRowCell[] = [];

        if (!this.vehicleFilter || !this.vehicleFilter.vehicle) {
            reportCells.push(this.createCell(journey.vehicle ? journey.vehicle.Reg : "", ColumnDataType.String, this.standardColumns.stringColumn))
        }

        if (!this.driverFilter || !this.driverFilter.driver) {
            reportCells.push(this.createCell(journey.driver ? journey.driver.Name : "", ColumnDataType.String, this.standardColumns.stringColumn))
        }

        reportCells.push(this.createCell(this.veriDate.formatDate(journey.StartUtc), ColumnDataType.Date, this.standardColumns.dateColumn))
        reportCells.push(this.createCell(journey.startPlot?.LocationInformation?.LocationDescription, ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(this.veriDate.formatDate(journey.EndUtc), ColumnDataType.Date, this.standardColumns.dateColumn))
        reportCells.push(this.createCell(journey.endPlot?.LocationInformation?.LocationDescription, ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(journey.Distance, UnitConversionType.Distance, 2), ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(journey.duration, ColumnDataType.Duration, this.standardColumns.durationColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(journey.FuelUsedTotal, UnitConversionType.FuelCapacity, 2), ColumnDataType.String, this.standardColumns.stringColumn))
        reportCells.push(this.createCell(this.unitConversionService.convertUnitWithSuffix(journey.Cost, UnitConversionType.Currency, 2), ColumnDataType.String, this.standardColumns.stringColumn))

        return reportCells;
    }

    private createCell(data: any, dataType: ColumnDataType, sourceHeader: IColumnHeader): IReportRowCell {
        const reportCell: IReportRowCell = {
            Data: data,
            Contents: data,
            CellDataType: dataType,
            DataSourceType: sourceHeader.DataSourceType,
            DataSourceValue: sourceHeader.DataSourceValue,
            AlwaysShowOnTotalsRow: true
        };

        return reportCell;
    }

    private calculateJourneysCost() {
        if (!this.preferences) {
            return;
        }

        this.displayJourneys.forEach(journey => {
            this.calculateCost(journey);
        });

        this.calculateTotals();
    }

    private calculateCost(journey: IJourney) {
        const costValue = this.preferences.JourneyCost.Value;

        switch (this.preferences.JourneyCostType.Value) {
            case JourneyCostType.Distance:
                let distanceFactor = journey.Distance;

                if (this.preferences.DistanceMeasurement.Value == DistanceMeasurement.Miles) {
                    distanceFactor = journey.Distance / 1.609;
                }
                journey.Cost = this.unitConversionService.convert(distanceFactor * costValue, UnitConversionType.Currency, 2);
                break;
            case JourneyCostType.Hourly:

                journey.Cost = this.unitConversionService.convert(moment.duration(moment(journey.EndUtc).diff(journey.StartUtc)).asHours() * costValue, UnitConversionType.Currency, 2);

                break;
        }
    }

    private calculateTotals() {
        this.totalExpensedJourneys = 0;
        this.totalCost = 0;
        this.totalDistance = 0;
        this.totalFuelUsed = 0;
        this.totalDuration = 0;

        this.displayJourneys.forEach(journey => {
            if (journey.Expensed) {
                this.totalExpensedJourneys++;
                this.totalCost += journey.Cost;
                this.totalDistance += journey.Distance;
                this.totalDuration += journey.durationMoment.minutes();

                if (journey.FuelUsedTotal) {
                    this.totalFuelUsed += journey.FuelUsedTotal;
                }
            }
        });

        this.totalCost = this.unitConversionService.convert(this.totalCost, UnitConversionType.Currency, 2);
    }

    private filterJourneys() {
        this.displayJourneys = this.journeyService.filterJourneys(this.journeys, this.vehicleFilter, this.driverFilter, this.groupFilter);
        this.calculateJourneysCost();
    }
}

export class ExpensesComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = ComponentController;
        this.template = require("./expenses.html");
    }
}
